import {
  checkPasswordForBreaches,
  checkPasswordSecurity,
} from '@disco/utils/password'
import captureError from '@disco/utils/captureError'
import React, { useState } from 'react'
import { Box, Flex, Heading, PasswordTextField, TextField } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Label from '../label'
import Separator from '../separator'
import type { SignUpDetails } from '../types'
import Warning from '../warning'
import ExplainError from './explain-error'
import Terms from './terms'
const PASSWORD_STRENGTH = 'standard'
type Props = {
  initialEmail?: string
  onSignupUser: (details: SignUpDetails) => Promise<{
    status: string
  }>
  onBackToLogin: () => void
}

/**
 * SignupForm component for creating new user accounts
 *
 * Provides a signup form with email, password, and password confirmation fields.
 * Includes password strength validation, breach checking, and error handling.
 * Allows users to create a free DISCO Catalog account.
 *
 * @param props - Component props
 * @param props.initialEmail - Pre-filled email address (optional)
 * @param props.onSignupUser - Callback function to handle user signup with email and password
 * @param props.onBackToLogin - Callback function to navigate back to login form
 * @returns JSX element containing the signup form
 */
const SignupForm = ({
  initialEmail,
  onSignupUser,
  onBackToLogin,
}: Props): React.ReactElement<'form'> => {
  const [email, setEmail] = useState<string>(initialEmail || '')
  const [newPassword, setNewPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async e => {
    e.preventDefault()
    const passwordSecure = checkPasswordSecurity(newPassword, PASSWORD_STRENGTH)

    if (!passwordSecure) {
      setError('WEAK_PASSWORD')
      return
    }

    if (newPassword !== passwordConfirm) {
      setError('The password confirmation does not match.')
      return
    }

    try {
      const numBreached = await checkPasswordForBreaches(newPassword)

      if (numBreached) {
        setError('breached_password')
        return
      }
    } catch (err) {
      console.error('checkPasswordForBreaches error', err) // ignore failures

      captureError(err)
    }

    setLoggingIn(true)

    try {
      let res = await onSignupUser({
        email: email.trim(),
        password: newPassword,
      })
      console.log('[signup] response', res)

      if (res.status !== 'success') {
        setError(res.status)
      }
    } catch (err) {
      console.error('[signup] error', err)
      setError(err.res)
    }

    setLoggingIn(false)
  }

  return (
    <Flex asChild direction="column" gap="3">
      <form onSubmit={handleSubmit}>
        <Heading align="center" mt="0">
          Create a free DISCO&nbsp;Catalog&nbsp;account
        </Heading>
        <Box>
          <Label htmlFor="email" caption="Your email" />
          <TextField.Root
            size="3"
            id="email"
            type="email"
            autoComplete="email"
            disabled={!!initialEmail || loggingIn}
            value={email}
            onChange={v => setEmail(v.target.value)}
            placeholder="you@company.com"
            autoCapitalize="off"
          />
        </Box>
        <Box>
          <Label htmlFor="newPassword" caption="Create password" />
          <PasswordTextField
            size="3"
            id="newPassword"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={v => setNewPassword(v.target.value)}
            placeholder="Choose a password"
            strength={PASSWORD_STRENGTH}
            disabled={loggingIn}
          />
        </Box>

        <Box>
          <Label htmlFor="password" caption="Repeat password" />
          <PasswordTextField
            size="3"
            id="password"
            type="password"
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={v => setPasswordConfirm(v.target.value)}
            placeholder="Choose a password"
            disabled={loggingIn}
          />
        </Box>

        <ButtonContainer vertical>
          <Button
            appearance="primary"
            type="submit"
            large
            caption="Create account"
            progressIndicator={loggingIn}
            progressCaption="Creating account"
            fullWidth
            textCenter
          />
          {error && (
            <Warning>
              <ExplainError onBackToLogin={onBackToLogin} error={error} />
            </Warning>
          )}
          <Button
            caption="Sign in with DISCO account"
            onClick={onBackToLogin}
            large
            fullWidth
            textCenter
          />
          <Separator />
          <Terms />
        </ButtonContainer>
      </form>
    </Flex>
  )
}

export default SignupForm
